import { CheckCircleOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { REQUEST_STATUSES } from 'commons/types/common';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import { useCallback, useState } from 'react';
import AuthCode from 'react-auth-code-input';
import { useAppDispatch, useAppSelector } from 'stores/hooks';
import {
  confirmPhoneOtpAsync,
  getConfirmOtpRequestStatus,
  getConflictError,
  getOtpRequestStatus,
  getThrottleError,
  requestPhoneOtpAsync,
} from 'stores/user/userSlice';
import formatSeconds from 'commons/libs/duration_utils';

interface Dataprops {
  newPhone: string;
  setNewPhone: (value: string) => void;
  step: string;
  setStep: (value: string) => void;
}

export default function UpdatePhone({
  newPhone,
  setNewPhone,
  step,
  setStep,
}: Dataprops) {
  const dispatch = useAppDispatch();
  const otpRequestStatus = useAppSelector(getOtpRequestStatus);
  const confirmOtpRequestStatus = useAppSelector(getConfirmOtpRequestStatus);
  const throttleError = useAppSelector(getThrottleError);
  const conflictError = useAppSelector(getConflictError);
  const [isPhoneValid, setIsPhoneValid] = useState(false);

  const onFinish = useCallback(() => {
    const formattedPhone = newPhone.startsWith('+') ? newPhone : `+${newPhone}`;
    setNewPhone(formattedPhone);
    dispatch(requestPhoneOtpAsync({ phone: formattedPhone }));
    setStep('code');
  }, [newPhone, setNewPhone, dispatch, setStep]);

  const onPhoneChange = useCallback(
    (phone: string, newCountry: CountryData) => {
      setNewPhone(phone);
      const isValid = isValidPhoneNumber(
        phone.substring(2),
        newCountry.countryCode.toUpperCase() as CountryCode,
      );
      setIsPhoneValid(isValid);
    },
    [setNewPhone],
  );

  const resendCode = useCallback(() => {
    dispatch(requestPhoneOtpAsync({ phone: newPhone }));
  }, [dispatch, newPhone]);

  const onFinishCode = useCallback(
    (otp: string) => {
      if (otp.length === 6) {
        dispatch(confirmPhoneOtpAsync({ otp, phone: newPhone }));
      }
    },
    [dispatch, newPhone],
  );

  if (confirmOtpRequestStatus === REQUEST_STATUSES.SUCCESS) {
    return (
      <div className='flex justify-center text-center'>
        <div className='flex flex-col w-80 justify-center bg-white rounded-xl px-4 py-8 my-12'>
          <p className='font-bold text-xl'>Nouveau numéro confirmé</p>
          <CheckCircleOutlined className='text-7xl !text-[#12F16B] self-center' />
        </div>
      </div>
    );
  }

  return (
    <div className='flex text-center justify-center'>
      {step === 'code' && otpRequestStatus === REQUEST_STATUSES.SUCCESS ? (
        <div className='flex flex-col w-80 justify-center bg-white rounded-xl px-4 py-8 my-12'>
          <p className='font-bold text-xl'>Code de vérification</p>
          <p className='text-xs mb-8'>
            Entrez le code que nous venons de vous envoyer au {newPhone}
          </p>
          <AuthCode
            containerClassName='flex flex-row'
            inputClassName='bg-white border-secondary w-1/2 rounded-xl mx-1 text-xl font-bold mb-4 text-center'
            allowedCharacters='numeric'
            onChange={onFinishCode}
          />
          {confirmOtpRequestStatus === REQUEST_STATUSES.ERROR &&
            !conflictError && (
              <p className='text-[10px] mt-2 text-red-600'>code invalide</p>
            )}
          {confirmOtpRequestStatus === REQUEST_STATUSES.ERROR &&
            conflictError && (
              <p className='text-[10px] mt-2 text-red-600'>
                Ce numéro est déjà attribué à un autre compte.
              </p>
            )}
          <p className='text-[10px] mt-2'>Le code est valide pendant 15 min</p>
          <Button
            type='link'
            className='text-primary mt-6'
            onClick={resendCode}
          >
            <p className='text-primary text-xs'>Renvoyer le code</p>
          </Button>
        </div>
      ) : (
        <div>
          <h2 className='text-lg font-semibold mb-8 pt-6'>
            Renseignez un nouveau numéro de téléphone
          </h2>
          <PhoneInput
            specialLabel=''
            countryCodeEditable={false}
            onChange={onPhoneChange}
            value={newPhone}
            inputClass='!rounded-[16px] !h-14 phoneInputBorder focus:border-0'
            country='fr'
          />
          {otpRequestStatus === REQUEST_STATUSES.ERROR && (
            <p className='text-[10px] mt-2 text-red-600'>
              L&apos;envoi a échoué. Merci de réessayer ultérieurement.
            </p>
          )}
          {otpRequestStatus === REQUEST_STATUSES.THROTTLED && (
            <p className='text-[10px] mt-2 text-red-600'>
              Veuillez patienter {formatSeconds(throttleError)} avant de
              réessayer.
            </p>
          )}
          <Button
            type='primary'
            className='!rounded-[24px] !h-14 !font-semibold mt-8 w-full mb-4'
            disabled={!isPhoneValid}
            onClick={onFinish}
            loading={otpRequestStatus === REQUEST_STATUSES.PENDING}
          >
            Recevoir le SMS
          </Button>
        </div>
      )}
    </div>
  );
}
